import React, { useContext, useEffect } from 'react';
import useDeepCompareEffect from 'ahooks/lib/useDeepCompareEffect';
import PropTypes from 'prop-types';
import { Builder, BuilderBlocks } from '@builder.io/react/lite';
import { withChildren } from "@builder.io/react";
import { addIdAttribute } from "@glowforge/builder-components/lib/util/shared-configs";
import { useRouter } from 'next/router';
import {
  MagicSvgDispatchContext,
  MagicSvgContext,
} from '../../../context/MagicSvgContext/MagicSvgContext';
import { PAGES, magicSvgActions } from '../../../context/MagicSvgContext/magicSvgReducer';

function MagicSvgPageWrapper({
  dataTestId,
  overridePage,
  overriddenPage,
  builderBlock,
  builderState,
  generateButtonContent,
  deliverButtonContent,
  regenerateMessage,
  regenerateButtonContent,
  insufficientTokenModalId,
  forceInsufficientTokenMessage,
  ...pages
}) {
  const { dispatch } = useContext(MagicSvgDispatchContext);
  const { insufficientTokenAttempts, showBanner } = useContext(MagicSvgContext);

  const router = useRouter();
  const { page } = router.query;
  const currentView = pages[page || PAGES.LANDING];
  const {
    openModal,
    closeModal,
  } = useContext(builderState.context.ModalPriorityContext);

  useEffect(() => {
    if (insufficientTokenAttempts > 0 || (forceInsufficientTokenMessage && Builder.isEditing)) {
      openModal(insufficientTokenModalId);
    } else {
      closeModal(insufficientTokenModalId);
    }
  }, [
    insufficientTokenAttempts,
    openModal,
    insufficientTokenModalId,
    forceInsufficientTokenMessage,
    closeModal,
  ]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (showBanner) {
        dispatch({
          type: magicSvgActions.SET_SHOW_BANNER,
          showBanner: false,
        });
      }
    };

    router.events.on('routeChangeStart', handleRouteChange);

    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
    };
  }, [dispatch, router, showBanner]);


  useEffect(() => {
    if (!page) {
      router.replace({
        query: {
          ...router.query,
          page: PAGES.LANDING,
        },
      });
    }
    if (Builder.isEditing && overridePage) {
      router.replace({
        query: {
          ...router.query,
          page: overriddenPage,
        },
      });
    }
  }, [
    overridePage,
    overriddenPage,
    dispatch,
    page,
    router,
  ]);


  useDeepCompareEffect(() => {
    dispatch({
      type: magicSvgActions.SET_BUILDER_PAGE_ID,
      builderData: {
        builderPageId: builderBlock.id,
        generateButtonContent,
        deliverButtonContent,
        regenerateMessage,
        regenerateButtonContent,
      }
    });
  }, [
    dispatch,
    builderBlock.id,
    generateButtonContent,
    deliverButtonContent,
    regenerateMessage,
    regenerateButtonContent,
  ]);

  return (
    <div {...addIdAttribute(dataTestId)}>
      <BuilderBlocks
        parentElementId={builderBlock.id}
        blocks={currentView}
      />
    </div>
  );
};

MagicSvgPageWrapper.propTypes = {
  children: PropTypes.node,
  dataTestId: PropTypes.string,
  overridePage: PropTypes.bool,
  forceInsufficientTokenMessage: PropTypes.bool,
  overriddenPage: PropTypes.string,
  builderBlock: PropTypes.shape({
    id: PropTypes.string,
  }),
  builderState: PropTypes.shape({
    context: PropTypes.shape({
      REACT_ROOT: PropTypes.string,
      ModalPriorityContext: PropTypes.shape({}),
    }),
    state: PropTypes.shape({
      referrals: PropTypes.shape({
        referralCode: PropTypes.string,
      }),
      affiliates: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
  }).isRequired,
  generateButtonContent: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.any),
      PropTypes.elementType,
    ]),
    id: PropTypes.string,
  })),
  deliverButtonContent: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.any),
      PropTypes.elementType,
    ]),
    id: PropTypes.string,
  })),
  regenerateMessage: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.any),
      PropTypes.elementType,
    ]),
    id: PropTypes.string,
  })),
  regenerateButtonContent: PropTypes.arrayOf(PropTypes.shape({
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.any),
      PropTypes.elementType,
    ]),
    id: PropTypes.string,
  })),
  insufficientTokenModalId: PropTypes.string,
};

MagicSvgPageWrapper.defaultProps = {
  children: [],
  dataTestId: null,
  overridePage: false,
  overriddenPage: null,
  builderBlock: {
    id: '',
  },
  generateButtonContent: null,
  deliverButtonContent: null,
  regenerateMessage: null,
  regenerateButtonContent: null,
  insufficientTokenModalId: null,
  forceInsufficientTokenMessage: false,
};

export default withChildren(MagicSvgPageWrapper);
